import { ContentAPI } from 'publication-types'
import { httpGet } from 'common/helpers/http'
import { validateKeyTypes } from 'common/helpers/validation'
import { Paging } from 'common/__content-api/v4/paging'

/// needed for generating declarations
import { isFeatureEnabled } from 'common/feature-togglings/features'
import { DataLoaderGlobalParams } from 'common/data/resources'

export const validate = (value: any): ContentAPI.PublicationListingV4ResponseDTO => {
    const errors = validateKeyTypes('topic-list', value, {
        page_size: 'number',
        page: 'number',
        total: 'number',
        documents: [{}],
        morePublicationsAvailable: 'boolean',
    })

    if (errors) {
        throw new Error('Topic List response invalid\n    ' + errors.join('    \n'))
    }

    return value
}

export const validateExpanded = (
    value: any
): ContentAPI.PublicationExpandedListingV4ResponseDTO => {
    const errors = validateKeyTypes('topic-list', value, {
        page_size: 'number',
        page: 'number',
        total: 'number',
        documents: [{}],
        morePublicationsAvailable: 'boolean',
    })

    if (errors) {
        throw new Error('Topic Expanded List response invalid\n    ' + errors.join('    \n'))
    }

    return value
}

export interface Options {
    isSponsored?: boolean
    topics?: string[]
    parentTopics?: string[]
    excludeTopics?: string[]
    authors?: string[]
    publicationKind?: 'article' | 'gallery'
    paging?: Paging
    source?: string
    publishedSince?: string
    profiles?: boolean
    includePremium?: boolean
}

export const getTopicListing = (services: DataLoaderGlobalParams, options: Options) => {
    const include_premium =
        // option defaults to true, which is then confirmed/denied by the feature toggle
        // see commments in https://bitbucket.swmdigital.io/projects/PROD/repos/web/pull-requests/1501/overview
        options.includePremium !== false &&
        isFeatureEnabled(services.store.getState().toggles, 'digital-subscriptions', false)

    const excludeRegional = isFeatureEnabled(
        services.store.getState().toggles,
        'exclude-regional-topics-from-lists',
        false
    )

    const { paging = {} } = options
    const query = {
        page: paging.page || 1,
        page_size: paging.pageSize || 6,
        page_offset: paging.pageOffset || 0,
        isSponsored: options.isSponsored,
        kind: options.publicationKind,
        topics: options.topics,
        parentTopic: options.parentTopics,
        excludeTopics: options.excludeTopics,
        authors: options.authors,
        source: options.source,
        publishedSince: options.publishedSince,
        profiles: options.profiles,
        include_premium: include_premium || undefined,
        excludeRegional,
    }

    return httpGet({
        log: services.log,
        validate,
        baseUrl: services.config.contentApi,
        path: `v4/publication`,
        query,
        customHeaders: {
            caller: services.config.apiCallerHeader,
        },
    })
}

export const getExpandedTopicListing = (services: DataLoaderGlobalParams, options: Options) => {
    const include_premium =
        // option defaults to true, which is then confirmed/denied by the feature toggle
        // see commments in https://bitbucket.swmdigital.io/projects/PROD/repos/web/pull-requests/1501/overview
        options.includePremium !== false &&
        isFeatureEnabled(services.store.getState().toggles, 'digital-subscriptions', false)

    const excludeRegional = isFeatureEnabled(
        services.store.getState().toggles,
        'exclude-regional-topics-from-lists',
        false
    )

    const { paging = {} } = options
    const query = {
        page: paging.page || 1,
        page_size: paging.pageSize || 6,
        page_offset: paging.pageOffset || 0,
        isSponsored: options.isSponsored,
        kind: options.publicationKind,
        topics: options.topics,
        parentTopic: options.parentTopics,
        source: options.source,
        publishedSince: options.publishedSince,
        profiles: options.profiles,
        expand: 'articles',
        include_premium: include_premium || undefined,
        excludeRegional,
    }

    return httpGet({
        log: services.log,
        validate: validateExpanded,
        baseUrl: services.config.contentApi,
        path: `v4/publication`,
        query,

        customHeaders: {
            caller: services.config.apiCallerHeader,
        },
    })
}
